import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import {
  Plus,
  Headset,
  Clock,
  CheckCircle,
  List,
  XCircle,
  TrendingUp,
} from 'lucide-react';
import { FaChartLine } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useAtendimentos } from '../../hooks/useAtendimentos';
import { useFuncionarios } from '../../hooks/useFuncionarios';
import AtendimentoForm from './AtendimentoForm';
import AtendimentoCard from './AtendimentoCard';
import AtendimentoHistorico from './AtendimentoHistorico';
import AtendimentoToReserva from './AtendimentoToReserva';
import { AtendimentoFormData, Atendimento, ReservaFormData } from '../../types';
import { addReserva } from '../../services/firestoreService';
import { auth } from '../../firebase/config';
import MainHeader from '../Common/MainHeader/MainHeader';
import './Atendimento.css';
import ServicesRanking from './ServicesRanking';

const AtendimentoList: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.uid || '';

  const {
    atendimentos,
    atendimentosDoDia,
    loading,
    error,
    addAtendimento,
    finalizar,
    cancelar,
    atualizar,
    loadAtendimentosDoDia,
  } = useAtendimentos(userId);

  const { funcionarios, loading: loadingFuncionarios } = useFuncionarios(userId);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [atendimentoParaEditar, setAtendimentoParaEditar] = useState<Atendimento | null>(null);
  const [modalKey, setModalKey] = useState(0);
  const [viewMode, setViewMode] = useState<'em_andamento' | 'historico' | 'ranking'>('em_andamento');
  const [showReservaModal, setShowReservaModal] = useState(false);
  const [atendimentoParaReserva, setAtendimentoParaReserva] = useState<Atendimento | null>(null);

  // Filtrar atendimentos
  const atendimentosEmAndamento = atendimentosDoDia.filter(a => a.status === 'em_andamento');
  const atendimentosFinalizados = atendimentosDoDia.filter(a => a.status === 'finalizado');
  const atendimentosCancelados = atendimentosDoDia.filter(a => a.status === 'cancelado');

  const handleSaveAtendimento = async (data: AtendimentoFormData) => {
    const funcionario = funcionarios.find(f => f.id === data.funcionarioId);
    if (!funcionario) throw new Error('Funcionário não encontrado');

    await addAtendimento(data, funcionario.nome);
    // ❌ REMOVER: await loadAtendimentosDoDia(); 
    // O listener em tempo real atualizará automaticamente
  };

  const handleFinalizar = async (id: string, resultado: string, observacoes?: string) => {
    await finalizar(id, resultado, observacoes);
    // ❌ REMOVER: await loadAtendimentosDoDia();
    // O listener em tempo real atualizará automaticamente
  };

  const handleCancelar = async (id: string, motivo?: string) => {
    await cancelar(id, motivo);
    // ❌ REMOVER: await loadAtendimentosDoDia();
    // O listener em tempo real atualizará automaticamente
  };

  // Função para abrir edição
  const handleEditAtendimento = (atendimento: Atendimento) => {
    // Buscar o atendimento mais recente da lista para garantir dados atualizados
    const atendimentoAtualizado = atendimentosDoDia.find(a => a.id === atendimento.id) || atendimento;

    setAtendimentoParaEditar(atendimentoAtualizado);
    setModalKey(prev => prev + 1); // Força recriação do modal
    setShowEditModal(true);
  };

  // Função para salvar edição (não atualiza hora de início)
  const handleSaveEditAtendimento = async (data: AtendimentoFormData) => {
    try {
      if (!atendimentoParaEditar) return;

      const funcionario = funcionarios.find(f => f.id === data.funcionarioId);
      if (!funcionario) throw new Error('Funcionário não encontrado');

      // Atualizar apenas os campos permitidos
      const updateData: any = {
        clienteNome: data.clienteNome,
        funcionarioId: data.funcionarioId,
        funcionarioNome: funcionario.nome,
        tipoAtendimento: data.tipoAtendimento as any,
        observacoes: data.observacoes || ''
        // NÃO incluir horaInicio - mantém a original
      };

      // Adicionar resultado apenas se tiver valor
      if (data.resultado && data.resultado.trim()) {
        updateData.resultado = data.resultado;
      }

      // Se clienteId está vazio (cliente novo), remover o campo do banco
      if (data.clienteId && data.clienteId.trim()) {
        updateData.clienteId = data.clienteId;
      } else {
        // Para cliente novo, remover o clienteId do banco
        updateData.clienteId = null;
      }

      await atualizar(atendimentoParaEditar.id!, updateData);

      // ❌ REMOVER: await loadAtendimentosDoDia();
      // O listener em tempo real atualizará automaticamente

      setShowEditModal(false);
      setAtendimentoParaEditar(null);
    } catch (error) {
      console.error('Erro ao atualizar atendimento:', error);
      alert('Erro ao atualizar atendimento');
    }
  };

  // Função para criar reserva a partir de atendimento
  const handleCreateReserva = async (reservaData: ReservaFormData) => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Usuário não autenticado');

      const result = await addReserva({ ...reservaData, userId: uid } as any);
      if (result.success && result.id) {

        // Atualizar o atendimento com o ID da reserva
        if (atendimentoParaReserva && atendimentoParaReserva.id) {
          await atualizar(atendimentoParaReserva.id, { reservaId: result.id });
        }

        return { id: result.id };
      } else {
        throw new Error(result.error || 'Erro ao criar reserva');
      }
    } catch (error: any) {
      throw error;
    }
  };

  // Callback quando atendimento converteu em reserva
  const handleConverteuReserva = (atendimento: Atendimento) => {
    setAtendimentoParaReserva(atendimento);
    setShowReservaModal(true);
  };

  if (loading || loadingFuncionarios) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Carregando atendimentos...</p>
      </Container>
    );
  }

  return (
    <div >
      <MainHeader
        title="Atendimento"
        subtitle="Gerencie os atendimentos do dia com eficiência"
        icon={<Headset size={28} />}
      />

      {/* 🎯 Botões de Controle Modernos */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex gap-2">
          {[
            { key: 'em_andamento', label: 'Em Andamento', icon: <Clock size={18} /> },
            { key: 'historico', label: 'Histórico', icon: <List size={18} /> },
            { key: 'ranking', label: 'Ranking', icon: <TrendingUp size={18} /> },
          ].map(btn => (
            <button
              key={btn.key}
              onClick={() => setViewMode(btn.key as any)}
              className={`atendimento-btn atendimento-btn-outline ${viewMode === btn.key ? 'active' : ''
                }`}
            >
              {btn.icon}
              {btn.label}
              {btn.key === 'em_andamento' && (
                <Badge
                  bg={viewMode === btn.key ? 'light' : 'primary'}
                  text={viewMode === btn.key ? 'dark' : 'light'}
                  style={{ marginLeft: '0.25rem' }}
                >
                  {atendimentosEmAndamento.length}
                </Badge>
              )}
            </button>
          ))}
        </div>

        <button
          onClick={() => setShowFormModal(true)}
          className="atendimento-btn atendimento-btn-success"
        >
          <Plus size={20} />
          Iniciar Atendimento
        </button>
      </div>

      {/* 🔹 Erro */}
      {error && (
        <Alert variant="danger" dismissible>
          {error}
        </Alert>
      )}


      {/* 📋 Conteúdo Principal */}
      {viewMode === 'em_andamento' && (
        <div>
          <div className="mb-4" >
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaChartLine className="text-primary" size={20} />
              <h5 className="mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
                Resumo do Dia
              </h5>
              <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                {new Date().toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                })}
              </span>
            </div>

            <div className="stats-grid">
              {[
                { label: 'Em Andamento', count: atendimentosEmAndamento.length, className: 'stat-card-warning', icon: Clock },
                { label: 'Finalizados', count: atendimentosFinalizados.length, className: 'stat-card-success', icon: CheckCircle },
                { label: 'Cancelados', count: atendimentosCancelados.length, className: 'stat-card-danger', icon: XCircle },
                { label: 'Total do Dia', count: atendimentosDoDia.length, className: 'stat-card-primary', icon: Headset },
              ].map(({ label, count, className, icon: Icon }) => (
                <div key={label} className={`stat-card ${className}`}>
                  <div className="stat-card-icon">
                    <Icon size={28} />
                  </div>
                  <div className="stat-card-value">{count}</div>
                  <div className="stat-card-label">{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <Clock size={22} style={{ color: 'var(--success-green)' }} />
            <h5 className="mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
              Atendimentos em Andamento
            </h5>
          </div>

          {atendimentosEmAndamento.length === 0 ? (
            <div className="empty-state">
              <Headset size={64} />
              <h5>Nenhum atendimento ativo no momento</h5>
              {/* <p>Clique no botão abaixo para iniciar um novo atendimento.</p> */}
              {/* <button
                className="atendimento-btn atendimento-btn-primary"
                onClick={() => setShowFormModal(true)}
              >
                <Plus size={20} />
                Iniciar Novo Atendimento
              </button> */}
            </div>
          ) : (
            <Row>
              {atendimentosEmAndamento.map(atendimento => (
                <Col key={atendimento.id} xs={12} md={6} lg={6} className="mb-3">
                  <AtendimentoCard
                    atendimento={atendimento}
                    onFinalizar={handleFinalizar}
                    onCancelar={handleCancelar}
                    onEditAtendimento={handleEditAtendimento}
                    onConverteuReserva={handleConverteuReserva}
                  />
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}
      {viewMode === 'historico' && (
        <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="historico-section">
            <div className="historico-title">
              <List size={24} />
              <h5>Histórico de Atendimentos</h5>
            </div>
            <AtendimentoHistorico />
          </div>
        </div>
      )}
      {viewMode === 'ranking' && (
        <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="historico-section">
            <div className="historico-title">
              <TrendingUp size={24} />
              <h5>Ranking de Atendimentos por Funcionário</h5>
            </div>
            <ServicesRanking atendimentos={atendimentos} />
          </div>
        </div>
      )}

      {/* 🔹 Modal de Criar Atendimento */}
      <AtendimentoForm
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        onSave={handleSaveAtendimento}
        funcionarios={funcionarios}
      />

      {/* 🔹 Modal de Editar Atendimento */}
      <AtendimentoForm
        key={`edit-${atendimentoParaEditar?.id}-${modalKey}`} // Força recriação quando muda o atendimento
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setAtendimentoParaEditar(null);
        }}
        onSave={handleSaveEditAtendimento}
        funcionarios={funcionarios}
        modoEdicao={true}
        atendimentoParaEditar={atendimentoParaEditar || undefined}
      />

      {/* 🔹 Modal de Criar Reserva a partir de Atendimento */}
      <AtendimentoToReserva
        show={showReservaModal}
        onHide={() => {
          setShowReservaModal(false);
          setAtendimentoParaReserva(null);
        }}
        atendimento={atendimentoParaReserva}
        onCreateReserva={handleCreateReserva}
        onReservaCreated={() => {
          // Opcional: recarregar dados se necessário
        }}
      />
    </div>
  );
};

export default AtendimentoList;
